<template>
  <div class="old-globals auth-card">
    <md-card class="gray-3 bg">
      <md-progress-bar v-if="loading" md-mode="query" />
      <md-card-content>
        <img class="auth-logo" alt="VRpatients Logo" src="../../assets/images/VRP-logo-horizontal.svg">

        <div class="auth-tabs">
          <button
            type="button"
            class="auth-tab"
            :class="{ 'is-active': tab === 'login' }"
            @click="selectTab('login')"
          >
            Login
          </button>
          <button
            type="button"
            class="auth-tab"
            :class="{ 'is-active': tab === 'signup' }"
            @click="selectTab('signup')"
          >
            Create Account
          </button>
        </div>

        <div v-if="error" class="form-error">{{ errorMessage }}</div>

        <form v-if="tab === 'login'" @submit.prevent="login">
          <md-field>
            <md-input v-model.trim="email" placeholder="Email" />
          </md-field>
          <md-field>
            <md-input v-model="password" type="password" placeholder="Password" />
          </md-field>
          <md-button type="submit" class="md-raised md-primary auth-submit">Login</md-button>
          <div class="auth-link-row">
            <router-link v-if="hasResetPassword" class="auth-link" to="reset-password">
              Forgot password?
            </router-link>
          </div>
        </form>

        <div v-else-if="signupView !== 'form'" class="auth-notice">
          <p class="auth-notice-title">
            <template v-if="signupView === 'sent'">
              An invitation has been sent to {{ sentEmail }}
            </template>
            <template v-else>
              You already have a pending invitation to {{ sentEmail }}
            </template>
          </p>
          <p class="auth-notice-body">
            <template v-if="signupView === 'sent'">
              Follow the link inside to finish setting up your {{ sentAccountTypeLabel }} account.
            </template>
            <template v-else>
              Follow the link in your invite email to accept it.
            </template>
          </p>
          <md-button class="md-raised md-primary auth-submit" @click="backToLogin">
            Back to Login
          </md-button>
          <div class="auth-link-row">
            <a v-if="signupView === 'sent'" class="auth-link" href="#" @click.prevent="resend">
              Didn’t get it? Resend invite
            </a>
          </div>
        </div>

        <form v-else @submit.prevent="signup">
          <md-field>
            <md-input v-model.trim="signupEmail" type="email" placeholder="Email" required />
          </md-field>

          <div v-if="showManagedWarning" class="form-warning">
            <md-icon class="form-warning-icon">warning</md-icon>
            <div>
              <div class="form-warning-title">
                Your school or organization manages accounts centrally
              </div>
              <div>Your instructor sends an invite — you don’t need to create an account.</div>
            </div>
          </div>

          <md-field>
            <md-select v-model="accountType" placeholder="Select account type">
              <md-option value="EMS">EMS</md-option>
              <md-option value="NURSING">Nursing</md-option>
            </md-select>
          </md-field>

          <md-button
            v-if="showManagedWarning"
            type="button"
            class="md-raised md-primary auth-submit"
            @click="clearSignup"
          >
            Go Back
          </md-button>
          <md-button v-else type="submit" class="md-raised md-primary auth-submit">
            Create Account
          </md-button>

          <div class="auth-link-row">
            <a
              v-if="showManagedWarning"
              class="auth-link"
              href="#"
              @click.prevent="signupAnyway"
            >
              Create account anyway →
            </a>
          </div>
        </form>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import { isManagedDomain } from '../../config/managedDomains';

const accountTypeLabels = {
  EMS: 'EMS',
  NURSING: 'Nursing',
};

export default {
  name: 'login-form',
  props: {
    loading: { type: Boolean, default: false },
    error: { type: Object, default: null },
    invited: { type: Boolean, default: false },
    pending: { type: Boolean, default: false },
    initialTab: { type: String, default: 'login' }
  },
  data() {
    return {
      tab: this.initialTab,
      email: '',
      password: '',
      signupEmail: '',
      accountType: 'EMS',
      // The email/account type the invite was actually sent to, held separately
      // so the confirmation keeps reading correctly if the form is edited.
      sentEmail: '',
      sentAccountType: 'EMS',
      acknowledgedManagedDomain: false
    };
  },
  computed: {
    errorMessage() {
      const { error } = this;
      return (error && (error.message || error.error)) || error;
    },
    signupView() {
      if (this.invited) return 'sent';
      if (this.pending) return 'pending';
      return 'form';
    },
    showManagedWarning() {
      return !this.acknowledgedManagedDomain && isManagedDomain(this.signupEmail);
    },
    sentAccountTypeLabel() {
      return accountTypeLabels[this.sentAccountType] || accountTypeLabels.EMS;
    },
    hasResetPassword() {
      // The authoring tool mounts this form without the password reset routes.
      const routes = (this.$router && this.$router.options.routes) || [];
      return routes.some((route) => route.name === 'reset-password');
    }
  },
  watch: {
    initialTab(tab) {
      this.tab = tab;
    },
    signupEmail() {
      // Editing the address re-arms the warning for the new domain.
      this.acknowledgedManagedDomain = false;
    }
  },
  methods: {
    selectTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.$emit('tab-change', tab);
    },
    login() {
      this.$emit('login', this.email, this.password);
      this.$analytics.event('Login', 'login', this.email);
    },
    signup() {
      // Not written back to signupEmail: that would re-arm the domain warning
      // through the watcher and undo signupAnyway().
      const email = this.signupEmail.toLowerCase();
      this.sentEmail = email;
      this.sentAccountType = this.accountType;
      this.$emit('signup', { email }, this.accountType);
      this.$analytics.event('Signup', 'signup', email);
    },
    signupAnyway() {
      this.acknowledgedManagedDomain = true;
      this.$analytics.event('Signup', 'signup-managed-domain-anyway', this.signupEmail);
      this.signup();
    },
    clearSignup() {
      this.signupEmail = '';
      this.acknowledgedManagedDomain = false;
    },
    resend() {
      this.$emit('resend', { email: this.sentEmail }, this.sentAccountType);
    },
    backToLogin() {
      this.$emit('reset-signup');
      this.clearSignup();
      this.selectTab('login');
    }
  }
};
</script>

<style scoped>
.auth-card >>> .md-card {
  border-radius: 2px;
}

.auth-card >>> .md-card-content {
  padding: 46px 20px 20px;
}

.auth-logo {
  display: block;
  /* Sized so the mark is a little wider than the tab labels, per the comps. */
  width: 64%;
  margin: 0 auto 22px;
}

.auth-tabs {
  display: flex;
  margin-bottom: 20px;
}

.auth-tab {
  flex: 1;
  padding: 8px 0 12px;
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  color: #2b2b2b;
  cursor: pointer;
}

.auth-tab.is-active {
  border-bottom-color: #3f83c5;
}

/* The globals leave room for inline fields; here everything is full width. */
.auth-card >>> .md-field,
.auth-card >>> .form-error {
  margin-right: 0;
}

.auth-card >>> .md-input,
.auth-card >>> .md-input::placeholder {
  font-size: 15px;
}

.auth-card >>> .md-input {
  box-sizing: border-box;
  height: 32px;
  padding: 4px 12px !important;
}

.auth-card >>> .md-field + .md-field {
  margin-top: 15px;
}

.auth-submit {
  display: block;
  width: 100%;
  height: 32px;
  margin: 15px 0 0;
  box-shadow: none !important;
  font-size: 15px;
  font-weight: 700;
  text-transform: none;
}

/* Held open whether or not a link is showing, so the card is the same height
   on both tabs and does not shift when you switch. */
.auth-link-row {
  min-height: 22px;
  margin-top: 7px;
}

.auth-link {
  display: block;
  text-align: center;
  font-size: 15px;
  color: #3f83c5;
}

.form-warning {
  display: flex;
  align-items: center;
  margin: 15px 0;
  padding: 10px 14px;
  border-radius: 3px;
  background-color: #f2994a;
  color: #fff;
  font-size: 15px;
}

.form-warning-icon {
  /* vue-material centers icons with `margin: auto`. */
  margin: 0 12px 0 0 !important;
  color: #fff !important;
}

.form-warning-title {
  font-weight: 700;
}

.auth-notice {
  text-align: center;
  color: #000;
}

.auth-notice-title {
  margin: 36px 0 0;
  font-size: 15px;
  font-weight: 700;
}

.auth-notice-body {
  margin: 2px 0 0;
  font-size: 15px;
}

.auth-notice .auth-submit {
  margin-top: 31px;
}
</style>
